"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { normalizeLocale, splitLocaleFromPathname } from "../lib/routeUtils";

const getPopupLocale = (pathname) => normalizeLocale(splitLocaleFromPathname(pathname).locale);

export default function PalmatePopupLoader({ projectToken = "6c3bd493-adaf-45ac-8cff-51a4d892d099", config }) {
  const pathname = usePathname();
  const locale = getPopupLocale(pathname);
  const isAdmin = pathname?.startsWith("/admin");

  useEffect(() => {
    if (isAdmin) return;
    // The popup can finish loading after the page has hydrated.
    let attempts = 0;
    const syncLanguage = () => {
      if (typeof window.palActions?.setLanguage !== "function") return false;
      window.palActions.setLanguage(locale);
      return true;
    };
    if (syncLanguage()) return;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (syncLanguage() || attempts >= 40) window.clearInterval(timer);
    }, 500);
    return () => window.clearInterval(timer);
  }, [locale, isAdmin]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Skip popup injection on admin pages
    if (window.location.pathname.startsWith("/admin")) return;

    if (window.__PALMATE_POPUP_LOADED__ || document.querySelector("script[data-palmate-popup]")) return;

    const w = window;
    w._pproject = w._pproject || [];

    w._pproject.push(["_project_token", projectToken]);

    // Custom config entries (if any)
    if (config && typeof config === "object") {
      Object.entries(config).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          w._pproject.push(value);
        } else {
          w._pproject.push([key, value]);
        }
      });
    }

    w._pproject.push([
      "events",
      {
        onBeforeAuth: async function () {
          // The current page wins over a previously saved language preference.
          try {
            const lang = getPopupLocale(window.location.pathname);
            return { request: { customer_info: { locale: lang } } };
          } catch {
            return { request: { customer_info: { locale: "en" } } };
          }
        },
      },
    ]);

    // w._pproject.push(["url_query", ["custom_button_trigger=mobile"]]);
    // w._pproject.push(["lazy_load", true]);

    const script = document.createElement("script");
    script.src = "https://app.palmate.ai/iframe/popup.js";
    script.defer = true;
    script.dataset.palmatePopup = "true";
    script.onload = () => {
      w.__PALMATE_POPUP_LOADED__ = true;
      w.palActions?.setLanguage?.(getPopupLocale(window.location.pathname));
    };
    script.onerror = () => { script.remove(); };
    // script.onload = () => {
    //   w.__PALMATE_POPUP_LOADED__ = true;
    // };

    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript?.parentNode) firstScript.parentNode.insertBefore(script, firstScript);
    else document.head.appendChild(script);
  }, [projectToken, config]);

  return null;
}
